<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="view-results-panel">
    <div
      v-if="loading"
      class="flex justify-content-center align-items-center p-5"
    >
      <PrimeProgressSpinner />
    </div>

    <div v-else-if="entries.length === 0" class="text-center text-gray-500 p-5">
      <i class="ri-search-line" style="font-size: 3rem" aria-hidden="true"></i>
      <p class="mt-2">{{ $t("rechercheAvancee.noResults") }}</p>
    </div>

    <div v-else>
      <div v-for="entry in entries" :key="entry.id" class="view-result-item">
        <div class="result-main">
          <!-- Site badge with +/- actions -->
          <div v-if="getSiteName(entry)" class="result-site-row">
            <span class="result-site-badge">
              <i class="ri-building-line" aria-hidden="true" />
              {{ getSiteName(entry) }}
            </span>
            <template v-if="showLocationActions && getSiteElement(entry)">
              <PrimeButton
                icon="ri-add-circle-line"
                v-tooltip="
                  $t('rechercheAvancee.addAsIncludeSite', {
                    name: getSiteName(entry),
                  })
                "
                class="action-include location-action"
                text
                rounded
                size="small"
                @click.stop="addSiteAsLocation(entry, false)"
              />
              <PrimeButton
                icon="ri-indeterminate-circle-line"
                v-tooltip="
                  $t('rechercheAvancee.addAsExcludeSite', {
                    name: getSiteName(entry),
                  })
                "
                class="action-exclude location-action"
                text
                rounded
                size="small"
                @click.stop="addSiteAsLocation(entry, true)"
              />
            </template>
          </div>

          <div class="result-header">
            <i
              :class="getNodeIcon(entry)"
              class="result-icon"
              aria-hidden="true"
            />
            <a
              class="result-name"
              :title="entry.name"
              @click="$emit('select-node', entry)"
              >{{ entry.name }}</a
            >
          </div>

          <!-- Breadcrumb path with +/- actions -->
          <div v-if="entry.path && entry.path.elements" class="result-path-row">
            <ComplexBreadcrumb
              :path="buildBreadcrumbPath(entry)"
              :show-actions="false"
              :show-home="false"
              :is-in-search="true"
              mode="auto"
              :home-route="homeRoute"
              :workspace-route-name="workspaceRouteName"
              :folder-route-name="folderRouteName"
              class="result-breadcrumb"
            />
            <template v-if="showLocationActions">
              <PrimeButton
                icon="ri-add-circle-line"
                v-tooltip="
                  $t('rechercheAvancee.addAsIncludeFolder', {
                    name: getParentName(entry),
                  })
                "
                class="action-include location-action"
                text
                rounded
                size="small"
                @click.stop="addParentAsLocation(entry, false)"
              />
              <PrimeButton
                icon="ri-indeterminate-circle-line"
                v-tooltip="
                  $t('rechercheAvancee.addAsExcludeFolder', {
                    name: getParentName(entry),
                  })
                "
                class="action-exclude location-action"
                text
                rounded
                size="small"
                @click.stop="addParentAsLocation(entry, true)"
              />
            </template>
          </div>

          <!-- Metadata -->
          <div class="result-meta">
            <span v-if="entry.modifiedAt" class="meta-item">
              <i class="ri-time-line" aria-hidden="true" />
              {{ formatDate(entry.modifiedAt) }}
            </span>
            <span
              v-if="entry.content && entry.content.sizeInBytes"
              class="meta-item"
            >
              <i class="ri-hard-drive-line" aria-hidden="true" />
              {{ formatSize(entry.content.sizeInBytes) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <PrimePaginator
        :first="first"
        :rows="rowsPerPage"
        :total-records="totalRecords"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        @page="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { useUserViewStore } from "@/stores/userView.js";
import ComplexBreadcrumb from "@/components/breadcrumb/ComplexBreadcrumb.vue";
import Button from "primevue/button";
import Paginator from "primevue/paginator";
import ProgressSpinner from "primevue/progressspinner";
import Tooltip from "primevue/tooltip";
import iconeService from "@/services/IconeService.js";

export default {
  name: "ViewResultsPanel",
  components: {
    ComplexBreadcrumb,
    PrimeButton: Button,
    PrimePaginator: Paginator,
    PrimeProgressSpinner: ProgressSpinner,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalRecords: {
      type: Number,
      default: 0,
    },
    rowsPerPage: {
      type: Number,
      default: 25,
    },
    showLocationActions: {
      type: Boolean,
      default: true,
    },
    workspaceRouteName: {
      type: String,
      default: "espace",
    },
    folderRouteName: {
      type: String,
      default: "espace-folder",
    },
    folderRouteParamName: {
      type: String,
      default: "folderId",
    },
    homeRoute: {
      type: [String, Object],
      default: () => ({ name: "home" }),
    },
  },
  data() {
    return {
      first: 0,
    };
  },
  emits: ["page-change", "select-node"],
  methods: {
    getNodeIcon(entry) {
      return iconeService.getIcon(entry);
    },
    buildBreadcrumbPath(entry) {
      if (!entry.path || !entry.path.elements) {
        return [];
      }

      const elements = entry.path.elements.filter(
        (el) =>
          el.name !== "Company Home" &&
          el.name !== "documentLibrary" &&
          el.nodeType !== "st:sites" &&
          el.nodeType !== "sys:container",
      );

      let workspaceName = null;
      const path = elements.map((item) => {
        const pathItem = {
          id: item.id,
          name: item.name,
          nodeType: item.nodeType,
          isFolder:
            item.nodeType === "cm:folder" || item.nodeType === "st:site",
        };

        if (item.nodeType === "st:site") {
          workspaceName = item.name;
          pathItem.to = {
            name: this.workspaceRouteName,
            params: { id: item.name },
          };
        } else if (workspaceName) {
          pathItem.to = {
            name: this.folderRouteName,
            params: { id: workspaceName, [this.folderRouteParamName]: item.id },
          };
        } else {
          pathItem.to = {};
        }

        return pathItem;
      });

      // Add home at the beginning
      path.unshift({
        nodeType: "home",
        to:
          typeof this.homeRoute === "string"
            ? { name: this.homeRoute }
            : this.homeRoute,
      });

      return path;
    },
    getSiteElement(entry) {
      if (!entry.path || !entry.path.elements) {
        return null;
      }
      return entry.path.elements.find((el) => el.nodeType === "st:site");
    },
    getSiteName(entry) {
      const site = this.getSiteElement(entry);
      return site ? site.name : null;
    },
    getParentName(entry) {
      if (!entry.path || !entry.path.elements) {
        return "";
      }
      const parent = entry.path.elements[entry.path.elements.length - 1];
      return parent ? parent.name : "";
    },
    addParentAsLocation(entry, negation) {
      if (!entry.path || !entry.path.elements) {
        return;
      }
      const parentElements = entry.path.elements;
      const parent = parentElements[parentElements.length - 1];
      if (!parent) {
        return;
      }

      const store = useUserViewStore();
      store.addLocation({
        type: "Folder",
        value: `workspace://SpacesStore/${parent.id}`,
        negation,
        label: parent.name,
      });
    },
    addSiteAsLocation(entry, negation) {
      const site = this.getSiteElement(entry);
      if (!site) {
        return;
      }

      const store = useUserViewStore();
      store.addLocation({
        type: "Site",
        value: site.name,
        negation,
        label: site.name,
      });
    },
    formatDate(dateStr) {
      if (!dateStr) {
        return "";
      }
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    handlePageChange(event) {
      this.first = event.first;
      this.$emit("page-change", event.first);
    },
    formatSize(bytes) {
      if (!bytes || bytes === 0) {
        return "0 B";
      }
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },
  },
};
</script>

<style scoped lang="scss">
.view-results-panel {
  width: 100%;
}

.view-result-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--p-surface-200);
  transition: background-color 0.15s;
  gap: 0.75rem;

  &:last-child {
    border-bottom: none;
  }
}

.result-main {
  flex: 1;
  min-width: 0;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-icon {
  font-size: 1.25rem;
  color: var(--p-primary-color);
  flex-shrink: 0;
}

.result-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  color: var(--p-primary-color);

  &:hover {
    text-decoration: underline;
  }
}

.result-site-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.result-site-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--p-blue-700);
  background: var(--p-blue-50);
  border: 1px solid var(--p-blue-200);
  border-radius: 1rem;
  padding: 0.1rem 0.5rem;
}

.result-path-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.result-breadcrumb {
  font-size: 0.8rem;
  min-width: 0;

  :deep(.breadcrumb-text) {
    max-width: 8rem;
  }

  :deep(.breadcrumb-separator) {
    margin: 0 0.125rem;
  }

  :deep(.breadcrumb-icon) {
    font-size: 0.9rem;
  }
}

.result-meta {
  display: flex;
  gap: 1rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.location-action {
  opacity: 0;
  transition: opacity 0.15s;
}

.view-result-item:hover .location-action {
  opacity: 1;
}

.action-include {
  color: var(--p-green-500) !important;
}

.action-exclude {
  color: var(--p-red-500) !important;
}
</style>
